/**
 * Firebase client SDK — single initialisation point for the public site.
 *
 * Reads are served directly from Firestore (fast, device-cached, works offline
 * via the SDK's own persistence). Writes that need validation, spam protection
 * or email go through callable Cloud Functions in `asia-southeast1`.
 *
 * Only Firestore and Functions are initialised here, because only those are
 * needed by the public website. Auth and Storage are set up in
 * `admin/lib/firebase.js` instead, which keeps roughly 150 KB of SDK out of the
 * bundle every visitor downloads — they are only ever used by the CMS, and the
 * CMS is lazy-loaded.
 *
 * Configuration comes from build-time env vars so the same source tree can be
 * pointed at a staging project. See `frontend/.env.example`.
 */

import { initializeApp, getApps, getApp } from "firebase/app";
import { initializeFirestore, memoryLocalCache, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

export const FUNCTIONS_REGION = "asia-southeast1";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/**
 * Whether Firebase is usable at all. When it is not (a fresh clone with no
 * .env, or a misconfigured build) the service layer falls back to the bundled
 * default content instead of throwing — the marketing site keeps rendering.
 */
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId
);

export const useEmulators =
  process.env.REACT_APP_USE_EMULATORS === "true" && process.env.NODE_ENV !== "production";

let app = null;
let db = null;
let functions = null;

if (isFirebaseConfigured) {
  app = getApps().length ? getApp() : initializeApp(firebaseConfig);

  // Memory cache, deliberately — NOT multi-tab persistence.
  //
  // With persistent multi-tab caching, Firestore elects one open tab as the
  // "primary" and every other tab proxies its queries through that tab's
  // client. If the primary happens to be a tab opened before signing in — the
  // public site sitting alongside the CMS, which is exactly how the site gets
  // demonstrated — the admin tab's queries are executed with the primary tab's
  // stale credentials and come back "Missing or insufficient permissions".
  //
  // That failure cannot be cleared from the affected tab: reloading it, or
  // signing out and back in, does not touch the other tab's client. It matches
  // the reported symptom exactly, including why retrying never helped.
  //
  // Memory caching gives every tab its own independent client with no primary
  // election and no shared credentials. The cost is no offline persistence
  // across reloads, which this app does not rely on: reads are already memoised
  // in `services/_base.js`, and bundled content covers a failed fetch.
  db = initializeFirestore(app, { localCache: memoryLocalCache() });

  functions = getFunctions(app, FUNCTIONS_REGION);

  if (useEmulators) {
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
    // eslint-disable-next-line no-console
    console.info("[firebase] Connected to local emulators");
  }
} else if (process.env.NODE_ENV !== "test") {
  // eslint-disable-next-line no-console
  console.warn(
    "[firebase] Not configured — copy frontend/.env.example to frontend/.env. " +
      "The site will render with bundled fallback content and forms will be disabled."
  );
}

export { app, db, functions };
